<script setup lang="ts">
import {ref, onMounted, watch, Ref, computed} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
import AutoComplete from 'primevue/autocomplete';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import {useReferenceStore} from "../../stores/referenceStore";
import backendCall from "../../services/AxiosService";
import {toDashedTrainId} from "../../utilities/toDashedTrainId";
import {useToast} from "primevue/usetoast";
import checkTrainDepartDate from "@/utilities/date";
import {checkLastTwoAreNumbers} from "@/utilities/stringFunctions";
import {CNApiLocationData} from "@/types/unit";
import { useDebounceSubmit } from '@/composables/useDebounceSubmit';
import { formatDateForAPI } from '@/utilities/dateUtils';
import contactService, { type Contact } from '@/services/ContactService';
import CreateContactDialog from '@/components/contacts/CreateContactDialog.vue';
import Chip from 'primevue/chip';

const referenceStore = useReferenceStore();

const toast = useToast();

const visible = defineModel('visible');

// Watch for dialog visibility and load companies when opened
watch(visible, async (newValue) => {
  if (newValue) {
    console.log('Dialog opened, loading data...');
    // Dialog is opening, load companies data
    await loadCompanies();
    
    // Also ensure tracks are loaded
    if (!referenceStore.tracks || referenceStore.tracks.length === 0) {
      console.log('Loading tracks on dialog open...');
      await referenceStore.fetchReferenceData();
    }
  }
});

const emit = defineEmits(["update-requests"]);

interface TemperatureData {
  value: number | null;
  unit: 'C' | 'F' | null;
  rawInput: string;
}

// interface UnitCheckData {
//   unitNumber: string;
//   returnTemp: TemperatureData;
//   billedSetpoint: TemperatureData;
//   actualSetpoint: TemperatureData;
//   supplyTemp: TemperatureData;
//   truckNumber: number | undefined;
//   fuelAdded: number | null;
//   startFuelLevel: string | null;
//   endFuelLevel: string | null;
//   alarms: [];
//   notes: string;
//   eventCode: string | null;
//   unitType: string | null;
//   browserCoordinates: {},
//   onRvr: boolean,
//   inDefrost: boolean
// }

interface RequestData {
  unitNumber: string;
  dateAdded: Date | null;
  carNumber: string | null;
  waybill: number;
  trainId: string | null;
  trainOriginDepartureDate: string | null;
  etaToMidpoint: Date | null;
  railway: string;
  sequence: number | null;
  track: string | null;
  current_location: string | null;
  billedSetpoint: TemperatureData;
  payer: number | null;
  leg: string | null;
  isFuelRequest: boolean;
  isRepairRequest: boolean;
  isCheckRequest: boolean;
  requestNotes: string;
  equipment_type: string;
  clipNumber: string | null;
}


const invalidFields = ref(new Set<string>());


type TemperatureFieldKey = keyof Pick<RequestData, 'billedSetpoint'>;

const trainOriginDepartureDate = ref();

const trainOriginDepartureDateStripped = computed(() => {
  return trainOriginDepartureDate.value ? formatDateForAPI(trainOriginDepartureDate.value) : null;
});

const form: Ref<RequestData> = ref({
  unitNumber: '',
  dateAdded: new Date(), // Initialize with Date object for proper Calendar component handling
  carNumber: null,
  waybill: 0,
  trainId: '',
  trainOriginDepartureDate: null,
  etaToMidpoint: null,
  railway: 'CN',
  sequence: null,
  track: null,
  current_location: null,
  billedSetpoint: {
    value: null,
    unit: null,
    rawInput: ''
  },
  payer: null,
  leg: '',
  isFuelRequest: false,
  isRepairRequest: false,
  isCheckRequest: false,
  requestNotes: '',
  equipment_type: '',
  clipNumber: null
});

watch(trainOriginDepartureDate, (newValue) => {
  if (newValue) {
    // Format the date to YYYY-MM-DD in UTC for consistency
    form.value.trainOriginDepartureDate = formatDateForAPI(new Date(newValue));
  } else {
    form.value.trainOriginDepartureDate = null;
  }
});

// Define interfaces for better type safety
interface Waybill {
  waybill_id: number;
  waybill_time?: string;
  train_id?: string;
  railway?: string;
  sequence?: number;
}

interface Track {
  id: number;
  name: string;
  is_special_risk?: boolean;
  special_risk_text?: string;
}

// Update refs with proper types
const waybills: Ref<Waybill[]> = ref([]);
const payers: Ref<{ label: string; value: number }[]> = ref([]);

// Add loading state variable for companies
const isLoadingCompanies = ref(false);

// Load companies from reference store
const loadCompanies = async () => {
  try {
    isLoadingCompanies.value = true;
    // Fetch companies data if not already loaded
    await referenceStore.fetchCompanies();


    // Ensure companies is an array before mapping
    const companies = Array.isArray(referenceStore.companies) ? referenceStore.companies : [];

    // Map company data to dropdown format
    payers.value = companies.map(company => ({
      label: company.name,
      value: company.id
    }));

  } catch (error) {
    console.error('Error loading companies:', error);
    // Initialize payers as empty array in case of error
    payers.value = [];
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load companies data',
      life: 3000
    });
  } finally {
    isLoadingCompanies.value = false;
  }
};

const railways = ref(['CN']);

const unitLocation = ref<CNApiLocationData[]>([]);
const showWaybillSection = ref(true);
const showTrainInfoSection = ref(true);
const cnApiData = ref();
const selectedWaybill = ref();


// Add loading state variables
const isLoadingWaybills = ref(false);
const isLoadingUnitInfo = ref(false);

// Result Recipients state
const selectedRecipients = ref<Contact[]>([]);
const recipientSearchQuery = ref('');
const contactSuggestions = ref<Contact[]>([]);
const isSearchingContacts = ref(false);
const showCreateContactDialog = ref(false);
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const fetchUnitInfo = async () => {
  // Skip if unit number is empty
  if (!form.value.unitNumber || form.value.unitNumber.trim() === '') {
    return;
  }

  try {
    selectedWaybill.value = {};
    form.value.sequence = null;

    // Add loading states
    isLoadingWaybills.value = true;
    isLoadingUnitInfo.value = true;

    // Make API calls in parallel with proper error handling
    const [fetchWaybillsCall, fetchApiInfoCall, fetchEquipmentInfoCall] = await Promise.allSettled([
      backendCall.get(`/waybills/by-unit-number/${form.value.unitNumber}`),
      backendCall.get(`units/cnquery?unit_number=${form.value.unitNumber}`),
      backendCall.get(`equipment/lookup?unit_number=${form.value.unitNumber}`)
    ]);

    // Process waybills if successful
    if (fetchWaybillsCall.status === 'fulfilled') {
      waybills.value = Array.isArray(fetchWaybillsCall.value.data) ? fetchWaybillsCall.value.data : [];
      waybills.value.unshift({waybill_id: 0});
      form.value.waybill = waybills.value?.[1]?.waybill_id || 0;
    } else {
      waybills.value = [{waybill_id: 0}];
    }

    // Process CN API data if successful
    if (fetchApiInfoCall.status === 'fulfilled') {
      cnApiData.value = Array.isArray(fetchApiInfoCall.value.data) ? fetchApiInfoCall.value.data : [];
      unitLocation.value = cnApiData.value;
      
      if (cnApiData.value?.[0]?.Event?.Location?.ReportedBy === 'Train') {
        form.value.trainId = cnApiData.value[0].Event.Location.ReportedId;
      }
      
      form.value.carNumber = cnApiData.value?.[0]?.FlatcarEquipmentId || null;
    } else {
      cnApiData.value = [];
      unitLocation.value = [];
    }

    // Process equipment info if successful
    if (fetchEquipmentInfoCall.status === 'fulfilled' && fetchEquipmentInfoCall.value.data?.data) {
      const equipmentInfo = fetchEquipmentInfoCall.value.data.data;
      if (equipmentInfo.isContainer) {
        form.value.equipment_type = 'container';
      } else if (equipmentInfo.isPowerpack) {
        form.value.equipment_type = 'powerpack';
      } else if (equipmentInfo.isValidatedClipOnNumber) {
        form.value.equipment_type = 'clip';
      } else {
        form.value.equipment_type = '';
      }
    } else {
      form.value.equipment_type = '';
    }
  } catch (error) {
    console.error('Error fetching unit info:', error);
    // Don't show error toast as some API calls failing is expected
    // Initialize with safe defaults
    waybills.value = [{waybill_id: 0}];
    cnApiData.value = [];
    unitLocation.value = [];
    form.value.equipment_type = '';
  } finally {
    isLoadingWaybills.value = false;
    isLoadingUnitInfo.value = false;
  }
};


watch(() => form.value.waybill, (newValue) => {
  if (newValue !== 0) {
   selectedWaybill.value = waybills.value.find((waybill: {waybill_id: number}) => waybill.waybill_id === newValue);
   form.value.sequence = selectedWaybill.value.sequence;
  } else {
    selectedWaybill.value = {};
    form.value.sequence = null;
  }
});

const waybillDisplay = (waybill: any) => {
  if (waybill.waybill_id === 0) {
    return 'New Waybill';
  }
  const waybillDate = new Date(waybill.waybill_time);
  const dashedTrainId = toDashedTrainId(waybill.train_id);
  return `${waybill.waybill_id} - ${waybillDate.toLocaleDateString()} - ${dashedTrainId}`;
};

const toggleTrainInfoSection = () => {
  showTrainInfoSection.value = form.value.waybill === 0;
};


function validateTemperatureField(field: TemperatureFieldKey): boolean {
  const data = form.value[field] as TemperatureData;
  if (!data.rawInput) {
    invalidFields.value.delete(field);
    return true; // Consider empty field as valid
  }

  const match = data.rawInput.match(/^(-?\d+(\.\d+)?)(C|F)$/i);
  if (match) {
    const [, numPart, , unitPart] = match;
    const numValue = parseFloat(numPart);
    const unitValue = unitPart.toUpperCase() as 'C' | 'F';

    form.value[field] = {
      value: numValue,
      unit: unitValue,
      rawInput: data.rawInput
    };

    invalidFields.value.delete(field);
    return true;
  }

  invalidFields.value.add(field);
  toast.add({
    severity: 'error',
    summary: 'Invalid Temperature',
    detail: `${field} must be a number followed by C or F`,
    life: 5000
  });
  return false;
}

// ============================================
// Contact Search & Recipient Management
// ============================================

const searchContacts = async (event: { query: string }) => {
  const query = event.query?.trim();

  // Clear timer if exists
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }

  // Require minimum 2 characters
  if (!query || query.length < 2) {
    contactSuggestions.value = [];
    return;
  }

  // Debounce the search
  searchDebounceTimer = setTimeout(async () => {
    try {
      isSearchingContacts.value = true;
      const results = await contactService.searchContacts(query, 10);

      // Filter out already-selected contacts
      const selectedIds = new Set(selectedRecipients.value.map(c => c.id));
      contactSuggestions.value = results.filter(c => !selectedIds.has(c.id));
    } catch (error) {
      console.error('Error searching contacts:', error);
      contactSuggestions.value = [];
    } finally {
      isSearchingContacts.value = false;
    }
  }, 300);
};

const addRecipient = (contact: Contact) => {
  if (!selectedRecipients.value.find(c => c.id === contact.id)) {
    selectedRecipients.value.push(contact);
  }
  recipientSearchQuery.value = '';
  contactSuggestions.value = [];
};

const removeRecipient = (contactId: number) => {
  selectedRecipients.value = selectedRecipients.value.filter(c => c.id !== contactId);
};

const handleContactCreated = (contact: Contact) => {
  addRecipient(contact);
  showCreateContactDialog.value = false;
};

// Create the debounced submit handler
const submitRequest = async () => {
  // Validate unit number is required and must be exactly 10 characters
  if (!form.value.unitNumber || form.value.unitNumber.trim() === '') {
    throw new Error('Unit Number is required and cannot be empty');
  }
  
  if (form.value.unitNumber.trim().length < 7 || form.value.unitNumber.trim().length > 10) {
    throw new Error('Unit Number must be between 7 and 10 characters');
  }
  
  // Validate description is required
  if (!form.value.requestNotes || form.value.requestNotes.trim() === '') {
    throw new Error('Description is required and cannot be empty');
  }
  
  // Validate waybill is selected
  if (form.value.waybill === null || form.value.waybill === undefined) {
    throw new Error('Please select a waybill');
  }
  
  if (invalidFields.value.size > 0) {
    throw new Error('Please enter valid temperatures');
  }

  // Format the date for API submission
  const requestData = {
    ...form.value,
    dateAdded: form.value.dateAdded ? formatDateForAPI(form.value.dateAdded) : null,
    // Include recipient contact IDs if any are selected
    recipientContactIds: selectedRecipients.value.length > 0
      ? selectedRecipients.value.map(c => c.id)
      : undefined
  };

  await backendCall.post("requests", requestData);

  // Reset recipients after successful submission
  selectedRecipients.value = [];
  recipientSearchQuery.value = '';

  emit('update-requests');
  visible.value = false;
};

// Use the debounced submit composable
const { isSubmitting, submit: handleSubmit } = useDebounceSubmit(submitRequest, {
  trackDuplicates: true
});

const trackSuggestions = ref<string[]>([]);
const allTracks = ref<Track[]>([]);

watch(() => referenceStore.tracks, (newValue) => {
  allTracks.value = newValue;
  console.log('Tracks loaded:', newValue?.length || 0, 'tracks');
  if (newValue && newValue.length > 0) {
    console.log('Sample track object:', newValue[0]);
  }
}, { immediate: true });


const searchTracks = (event: { query: string }) => {
  console.log('searchTracks called with query:', event.query);
  
  // Test with hardcoded suggestions first
  const testSuggestions = ['Track 1', 'Track 2', 'Track 3', 'Main Track', 'Side Track'];
  
  try {
    const query = event.query.trim().toLowerCase();

    if (query.length === 0) {
      // Show all suggestions when no query
      trackSuggestions.value = [...testSuggestions];
    } else {
      // Filter suggestions
      trackSuggestions.value = testSuggestions.filter(track =>
        track.toLowerCase().includes(query)
      );
    }
    
    console.log('trackSuggestions.value:', trackSuggestions.value);
    
    // Now try with real data
    if (Array.isArray(allTracks.value) && allTracks.value.length > 0) {
      console.log('Using real tracks data, count:', allTracks.value.length);
      console.log('First track:', allTracks.value[0]);
      const upperQuery = query.toUpperCase();
      
      // Check what property the track uses for display
      const firstTrack = allTracks.value[0];
      const nameProperty = firstTrack.name ? 'name' : 
                          firstTrack.track_name ? 'track_name' : 
                          firstTrack.description ? 'description' : 
                          'id';
      
      console.log('Using property:', nameProperty);
      
      if (query.length === 0) {
        trackSuggestions.value = allTracks.value.map(track => String(track[nameProperty] || track.id));
      } else {
        trackSuggestions.value = allTracks.value
          .filter((track: any) => {
            const trackName = String(track[nameProperty] || track.id || '');
            return track.id.toString().toUpperCase().includes(upperQuery) || 
                   trackName.toUpperCase().includes(upperQuery);
          })
          .map(track => String(track[nameProperty] || track.id));
      }
      console.log('Real track suggestions:', trackSuggestions.value);
    }
  } catch (error) {
    console.error('Error searching tracks:', error);
    trackSuggestions.value = [];
  }
};



const fillTrainOriginDepartureDate = () => {
  if (form?.value?.trainId && form.value.trainId.length > 6 && form.value.trainId.slice(-1) !== '-' && checkLastTwoAreNumbers(form.value.trainId)) {
    trainOriginDepartureDate.value = checkTrainDepartDate(form.value.trainId);
  } else {
    trainOriginDepartureDate.value = null;
  }
}

watch(() => form.value.trainId, (newValue) => {
  fillTrainOriginDepartureDate();
});

// Computed property to determine if clip number field should be shown
const showClipNumberField = computed(() => {
  return form.value.equipment_type === 'container' || form.value.equipment_type === 'clip';
});

// Watch for equipment type changes to clear clip number if not applicable
watch(() => form.value.equipment_type, (newType) => {
  if (newType !== 'container' && newType !== 'clip') {
    form.value.clipNumber = null;
  }
});

// Computed property to check if unit number is valid
const isUnitNumberValid = computed(() => {
  const trimmed = form.value.unitNumber?.trim() || '';
  return trimmed !== '' && trimmed.length >= 7 && trimmed.length <= 10;
});

// Computed property to check if description is valid
const isDescriptionValid = computed(() => {
  return form.value.requestNotes && form.value.requestNotes.trim() !== '';
});

// Computed property to check if waybill is selected
const isWaybillSelected = computed(() => {
  return form.value.waybill !== null && form.value.waybill !== undefined;
});

// Track if unit number field has been touched
const unitNumberTouched = ref(false);

// Track if description field has been touched
const descriptionTouched = ref(false);

// Computed property to determine if form can be submitted
const canSubmit = computed(() => {
  // Don't allow submission while loading unit info
  if (isLoadingWaybills.value || isLoadingUnitInfo.value || isLoadingCompanies.value) {
    return false;
  }
  return isUnitNumberValid.value && isDescriptionValid.value && isWaybillSelected.value && invalidFields.value.size === 0;
});

onMounted(async () => {
  console.log('NewRequestModal mounted');
  console.log('Initial tracks from store:', referenceStore.tracks);
  
  // await fetchAlarmList()
  if (!referenceStore.tracks || referenceStore.tracks.length === 0) {
    console.log('Fetching reference data...');
    await referenceStore.fetchReferenceData();
    console.log('After fetch, tracks:', referenceStore.tracks);
  }

  // Also load companies data on mount as a fallback
  await loadCompanies();
});
</script>

<template>
  <Dialog v-model:visible="visible" :closable="true" modal header="New Request"
          :style="{ width: '40rem' }" data-testid="new-request-modal">
    <div class="bg-white dark:bg-neutral-900  p-6 rounded-lg shadow-md">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="col-span-1">
          <label for="unit_number" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unit
            Number: <span class="text-red-500">*</span></label>
          <InputText v-model="form.unitNumber" autofocus id="unit_number" placeholder="Unit Number" 
                     @blur="() => { unitNumberTouched = true; fetchUnitInfo(); }"
                     :class="{'w-full': true, 'p-invalid': !isUnitNumberValid && unitNumberTouched}"
                     data-testid="unit-number-input"/>
          <small v-if="!isUnitNumberValid && unitNumberTouched" class="p-error">
            <span v-if="!form.unitNumber || form.unitNumber.trim() === ''">Unit Number is required</span>
            <span v-else>Unit Number must be between 7 and 10 characters</span>
          </small>
        </div>
        <div class="col-span-1">
          <label for="billed_setpoint"
                 class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Setpoint:</label>
          <InputText v-model="form.billedSetpoint.rawInput" @blur="validateTemperatureField('billedSetpoint')"
                     id="billed_setpoint" placeholder="Setpoint"
                     class="w-full"
                     data-testid="setpoint-input"/>
        </div>
        <div v-if="showClipNumberField" class="col-span-1">
          <label for="clip_number" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Clip # (if attached):</label>
          <InputText v-model="form.clipNumber" id="clip_number" placeholder="Clip Number" class="w-full"/>
        </div>
        <div class="col-span-1">
          <label for="date_added" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date Added:</label>
          <Calendar v-model="form.dateAdded" autofocus id="date_added" dateFormat="yy-mm-dd" class="w-full"/>
        </div>
        </div>
        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600"/>
        </div>
        <div class="col-span-2">
          <label for="unit_location_div" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location:</label>
          <div id="unit_location_div" class="text-gray-900 dark:text-gray-100" data-testid="unit-location-display">
            {{ unitLocation?.[0]?.Event?.Description }} {{ unitLocation?.[0]?.Event?.Location?.Station }}
            {{ unitLocation?.[0]?.Event?.Location?.ProvState }} <span v-if="unitLocation?.[0]?.Event"> @ </span>
            {{ unitLocation?.[0]?.Event?.Time }}
            <div id="reported_by_train_div" class="text-gray-900 dark:text-gray-100 mt-1">
              {{
                unitLocation?.[0]?.Event?.Location?.ReportedBy ? unitLocation?.[0]?.Event?.Location?.ReportedBy + ':' : ''
              }}
              {{ unitLocation?.[0]?.Event?.Location?.ReportedId }}
            </div>
          </div>
        </div>
        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600"/>
        </div>
        <div v-if="showWaybillSection" class="col-span-2">
          <label for="waybill_dropdown"
                 class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Waybill:</label>
          <Dropdown v-model="form.waybill" :options="waybills" option-value="waybill_id"
                    :option-label="waybillDisplay"
                    placeholder="Select Waybill" @change="toggleTrainInfoSection" class="w-full"
                    data-testid="waybill-dropdown"
                    :loading="isLoadingWaybills">
            <template #option="slotProps">
              <div v-if="slotProps.option.waybill_id === 0">
                New Waybill
              </div>
              <div v-else>
                {{ slotProps.option.waybill_id }} - {{ new Date(slotProps.option.waybill_time).toLocaleDateString() }} -
                {{ toDashedTrainId(slotProps.option.train_id) }}
              </div>
            </template>
          </Dropdown>
        </div>
        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600"/>
        </div>
        <div v-if="form.waybill == 0" id="train-information-section"
             class="col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label for="train_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Train
              ID:</label>
            <InputText v-model.lazy="form.trainId" id="train_id" placeholder="Train Id" class="w-full"/>
          </div>
          <div>
            <label for="train_origin_departure_date"
                   class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Origin Departure
              Date:</label>
            <Calendar v-model="trainOriginDepartureDate" id="train_origin_departure_date"
                      dateFormat="yy-mm-dd" class="w-full"/>
          </div>
          <div>
            <label for="eta_to_midpoint" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ETA To
              Midpoint:</label>
            <Calendar v-model="form.etaToMidpoint" id="eta_to_midpoint" showTime dateFormat="yy-mm-dd"
                      class="w-full"/>
          </div>
          <div>
            <label for="railway"
                   class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Railway:</label>
            <Dropdown v-model="form.railway" id="railway" :options="railways" placeholder="Select Railway"
                      class="w-full"/>
          </div>
        </div>
        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600"/>
        </div>
        <!-- First row: Sequence and Car Number -->
        <div class="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="sequence-input" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sequence:</label>
            <InputNumber v-model="form.sequence" :disabled="selectedWaybill?.sequence" id="sequence-input" fluid :min="1" :max-fraction-digits="0" placeholder="SEQ" class="w-full"/>
          </div>
          <div>
            <label for="car-number-input" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Car:</label>
            <InputText v-model="form.carNumber" id="car-number-input" placeholder="Car #" class="w-full"/>
          </div>
        </div>
        <!-- Second row: Track and Location with proper spacing -->
        <div class="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="track-input" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Track:</label>
            <AutoComplete 
              v-model="form.track" 
              id="track-input" 
              :suggestions="trackSuggestions" 
              @complete="searchTracks" 
              placeholder="Track" 
              class="w-full" 
              data-testid="track-input"
            />
          </div>
          <div>
            <label for="location-input" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location:</label>
            <InputText v-model="form.current_location" id="location-input" placeholder="location" class="w-full" data-testid="location-input"/>
          </div>
        </div>
        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600"/>
        </div>
        <div class="col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="col-span-1">
          <label for="payer-input"
                 class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payer:</label>
          <Dropdown v-model="form.payer" id="payer-input" 
                    :options="payers" 
                    option-label="label" 
                    option-value="value"
                    placeholder="Select Payer" 
                    class="w-full"
                    :disabled="isLoadingCompanies"
                    data-testid="payer-dropdown">
            <template #empty>
              <div v-if="isLoadingCompanies" class="p-2 text-center">
                <i class="pi pi-spin pi-spinner mr-2"></i>
                <span>Loading payers...</span>
              </div>
            </template>
          </Dropdown>
        </div>
        <div class="col-span-1">
          <label for="leg-input" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Leg:</label>
          <InputText v-model="form.leg" id="leg-input" placeholder="Leg" class="w-full"/>
        </div>
        </div>
        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600"/>
        </div>
        <div class="col-span-2 flex justify-between items-center">
          <div class="flex items-center">
            <Checkbox v-model="form.isFuelRequest" id="fuel_request" :binary="true"/>
            <label for="fuel_request" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">Fuel Request</label>
          </div>
          <div class="flex items-center">
            <Checkbox v-model="form.isRepairRequest" id="repair_request" :binary="true"/>
            <label for="repair_request" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">Repair
              Request</label>
          </div>
          <div class="flex items-center">
            <Checkbox v-model="form.isCheckRequest" id="check_request" :binary="true"/>
            <label for="check_request" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">Check Request</label>
          </div>
        </div>
        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600"/>
        </div>
        <div class="col-span-2">
          <label for="request-description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description: <span class="text-red-500">*</span></label>
          <Textarea v-model="form.requestNotes" id="request-description" rows="5" autoResize 
                    @blur="descriptionTouched = true"
                    :class="{'w-full': true, 'p-invalid': !isDescriptionValid && descriptionTouched}"
                    data-testid="description-textarea"/>
          <small v-if="!isDescriptionValid && descriptionTouched" class="p-error">Description is required</small>
        </div>

        <!-- Result Recipients Section -->
        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600 my-4"/>
        </div>
        <div class="col-span-2">
          <div class="flex justify-between items-center mb-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Result Recipients
            </label>
            <Button
              type="button"
              icon="pi pi-plus"
              label="New Contact"
              class="p-button-text p-button-sm"
              @click="showCreateContactDialog = true"
              data-testid="create-contact-button"
            />
          </div>
          <AutoComplete
            v-model="recipientSearchQuery"
            :suggestions="contactSuggestions"
            @complete="searchContacts"
            @item-select="(e: any) => addRecipient(e.value)"
            optionLabel="name"
            placeholder="Search contacts by name, email, or phone..."
            fluid
            class="w-full"
            :loading="isSearchingContacts"
            data-testid="recipient-search"
          >
            <template #option="slotProps">
              <div class="flex flex-col">
                <span class="font-medium">{{ slotProps.option.name }}</span>
                <span v-if="slotProps.option.email" class="text-sm text-gray-500">{{ slotProps.option.email }}</span>
                <span v-if="slotProps.option.phone" class="text-sm text-gray-500">{{ slotProps.option.phone }}</span>
              </div>
            </template>
          </AutoComplete>

          <!-- Selected Recipients -->
          <div v-if="selectedRecipients.length > 0" class="flex flex-wrap gap-2 mt-3">
            <Chip
              v-for="contact in selectedRecipients"
              :key="contact.id"
              :label="contact.name"
              removable
              @remove="removeRecipient(contact.id)"
              class="bg-blue-100 dark:bg-blue-900"
            />
          </div>
        </div>

        <!-- Create Contact Dialog -->
        <CreateContactDialog
          v-model:visible="showCreateContactDialog"
          @contact-created="handleContactCreated"
        />

        <div class="col-span-2">
          <hr class="border-t border-gray-300 dark:border-gray-600 my-4"/>
        </div>
        <div class="col-span-2">
          <Button @click="handleSubmit" 
                  :loading="isSubmitting || isLoadingWaybills || isLoadingUnitInfo || isLoadingCompanies" 
                  :disabled="isSubmitting || !canSubmit" 
                  :label="isLoadingWaybills || isLoadingUnitInfo || isLoadingCompanies ? 'Loading...' : 'Submit'" 
                  class="w-full"
                  data-testid="submit-button"/>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.container {
  padding-top: 2rem;
}

.card {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}
</style>
